import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import { DialogChildContentWrapper } from '@shared/components/core/dialog/CoreDialogStyles';
import { useCurrentOrgAuthClientEditor } from '@shared/hooks/useCurrentOrgAuthClientEditor';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClientSecretDialog from './ClientSecretDialog';
import ClientAuthRedirectEditor from './editors/ClientAuthRedirectEditor';
import ClientNameEditor from './editors/ClientNameEditor';
import ClientOriginHostEditor from './editors/ClientOriginHostEditor';
import ClientTokenRedirectEditor from './editors/ClientTokenRedirectEditor';

export interface IClientCreateDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function ClientCreateDialog({ open, onClose }: IClientCreateDialogProps) {
    const { t } = useTranslation();
    const { editClient, handleSave, handleInputChange } = useCurrentOrgAuthClientEditor(null);
    const [showSecret, setShowSecret] = useState<boolean>(false);

    const handleSaveAndClose = async () => {
        const response = await handleSave();
        if (response.resultType == 'Ok') {
            onClose();
            setShowSecret(true);
        }
    };

    if (!editClient) return null;
    return (
        <>
            <ContentDialog
                fullWidth
                open={open}
                onClose={onClose}
                onCancel={onClose}
                title={t('common:organization.projects.clients.create')}
                description={t('common:organization.projects.clients.description')}
                onConfirm={handleSaveAndClose}>
                <DialogChildContentWrapper>
                    <ClientNameEditor
                        value={editClient.name ?? ''}
                        onUpdate={(field, value) => handleInputChange(field, value)}
                    />
                    <ClientOriginHostEditor
                        value={editClient.originHost}
                        onUpdate={(field, value) => handleInputChange(field, value)}
                    />
                    <ClientTokenRedirectEditor
                        tokenAdditionalRedirectUris={editClient.tokenAdditionalRedirectUris}
                        tokenRedirectUrlHost={editClient.tokenRedirectUrlHost}
                        onUpdate={(additionalField, additionalValue, hostField, hostValue) => {
                            handleInputChange(additionalField, additionalValue);
                            handleInputChange(hostField, hostValue);
                        }}
                    />
                    <ClientAuthRedirectEditor
                        additionalRedirectUris={editClient.additionalRedirectUris}
                        authCodeRedirectUrlHost={editClient.authCodeRedirectUrlHost}
                        onUpdate={(additionalField, additionalValue, hostField, hostValue) => {
                            handleInputChange(additionalField, additionalValue);
                            handleInputChange(hostField, hostValue);
                        }}
                    />
                </DialogChildContentWrapper>
            </ContentDialog>
            <ClientSecretDialog open={showSecret} secret={editClient.secret} onClose={() => setShowSecret(false)} />
        </>
    );
}
